import colors from '../configs/colors'
import theme from '../services/theme'

// Stable 32-bit hash of an id, so a list's color and shape never change.
const hashId = (id: string): number => {
    let hash = 0
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) >>> 0
    }
    return hash
}

// User-picked palette overrides (palette index by list id), mirrored in
// localStorage so any surface — including a deep-linked list page that renders
// before the lists service has loaded — resolves a list's color synchronously.
// The lists service keeps this map in step with the server rows; the id hash
// below stays the fallback for lists without a picked color.
const COLOR_KEY = 'papa-list-colors'
let colorOverrides: Record<string, number> = {}
try { colorOverrides = JSON.parse(localStorage.getItem(COLOR_KEY) || '{}') || {} } catch { /* corrupt — fall back to hashes */ }

const persistOverrides = () => localStorage.setItem(COLOR_KEY, JSON.stringify(colorOverrides))

// Set (or clear, with null) one list's picked color.
const setListColor = (id: string, index: number | null | undefined): void => {
    if (typeof index === 'number') colorOverrides[id] = index
    else delete colorOverrides[id]
    persistOverrides()
}

// Refresh the overrides from fetched rows (the server refresh). Only the given
// lists are touched, so ids the fetch doesn't know about are left alone.
const syncListColors = (lists: Array<{ id: string; color?: number | null }>): void => {
    for (const l of lists) {
        if (typeof l.color === 'number') colorOverrides[l.id] = l.color
        else delete colorOverrides[l.id]
    }
    persistOverrides()
}

// A given list always keeps the same hue across the home circles and its page; the
// actual pair (circle background + text) flips with the theme — pastel-on-light vs
// deep-jewel-on-night, picked from the index-aligned palettes.
// Which palette slot (hue) an id falls on: the picked override when set (and in
// range — the palette may change size across versions), else the stable id hash.
// The two palettes are index-aligned, so this is the same hue in either theme;
// the seed uses it to pick starter colors.
const paletteIndexForId = (id: string): number => {
    const picked = colorOverrides[id]
    if (typeof picked === 'number' && picked >= 0 && picked < colors.LIST_PALETTE.length) return picked
    // Unpicked lists hash into the original hues only (HASHED_HUES is frozen),
    // so growing the palette never recolors existing lists.
    return hashId(id) % colors.HASHED_HUES
}

const paletteForId = (id: string) => {
    const palette = theme.get() === 'dark' ? colors.LIST_PALETTE_DARK : colors.LIST_PALETTE
    return palette[paletteIndexForId(id)]
}
// The list's solid color (opaque). Used where a surface should stay solid — e.g. the
// share sheet. The grid circles and the open list page use surfaceForId instead.
const colorForId = (id: string): string => paletteForId(id).bg
const textForId = (id: string): string => paletteForId(id).text

// The list page foreground accent (colored buttons): the dark text hue on light,
// the vibrant circle color on night, so buttons stay legible either way.
const accentForId = (id: string): string =>
    theme.get() === 'dark' ? paletteForId(id).bg : paletteForId(id).text

// The fill for a list's surface — its circle on the grid, the same circle as it
// expands, and its open page. The solid palette color in both themes (opaque on
// night too, so the starfield stays outside the circles and the open page).
const surfaceForId = colorForId

// Whether a list is private, shared by me, or shared with me. Sharing is derived
// from membership: every list has the owner as a member, so member_count > 1 means
// someone else is on it. The viewer's `role` tells us which side of the share they're on.
export type ShareState = 'private' | 'shared-by-me' | 'shared-with-me'

const shareState = (role: 'owner' | 'member' | undefined, memberCount: number | undefined): ShareState => {
    if (role === 'member') return 'shared-with-me'
    return (memberCount ?? 1) > 1 ? 'shared-by-me' : 'private'
}

export default {
    paletteForId,
    paletteIndexForId,
    setListColor,
    syncListColors,
    colorForId,
    textForId,
    accentForId,
    surfaceForId,
    shareState,
}
